import { isEqual, isNil } from "lodash-es";

import { Operation } from "../types/schemas";
import { TranscriptSelection, TranscriptSelectionNode } from "../types/types";
import { assertNever } from "./assert-never";

function normalizeNodes(
  start: TranscriptSelectionNode | null,
  end: TranscriptSelectionNode | null,
): TranscriptSelection | null {
  if (isNil(start) && isNil(end)) {
    return null;
  }

  // snap to non null if 1 node is null
  if (isNil(start)) return { start: end, end, type: "Caret" };
  if (isNil(end)) return { start, end: start, type: "Caret" };

  return {
    type: isEqual(start, end) ? "Caret" : "Range",
    start,
    end,
  };
}

export function transformSelectionByOperation(
  selection: TranscriptSelection | null,
  op: Operation,
): TranscriptSelection | null {
  if (!selection) return null;
  if (!selection.start || !selection.end) return selection;

  const { path } = op;
  switch (op.type) {
    case "object-set":
      // Replacing the entire transcript makes all current selections invalid
      if (path.length === 1) {
        if (path[0] === "transcript") {
          return null;
        }
        return selection;
      }

      // Replacing the transcript of a token
      if (path.length === 5) {
        if (
          path[0] === "sliceMeta" &&
          path[2] === "tokenMeta" &&
          path[4] === "transcript"
        ) {
          const sliceIndex = +path[1];
          const tokenIndex = +path[3];

          const [start, end] = [selection.start, selection.end].map((node) => {
            if (
              node.tokenIndex === tokenIndex &&
              node.sliceIndex === sliceIndex &&
              node.type === "token"
            ) {
              return {
                tokenIndex,
                sliceIndex,
                textOffset: 0,
                type: "token" as const,
              };
            }
            return node;
          });

          return normalizeNodes(start, end);
        }
        return selection;
      }

      return selection;
    case "object-delete":
      // object delete isn't used on /transcript so return current selection
      return selection;
    case "array-insert": {
      // insert slice(s) into transcript, if node is at or after insertion point shift right by number of new slices
      if (path.length === 1 && path[0] === "sliceMeta") {
        const delta = op.values.length;
        const { index } = op;

        const [start, end] = [selection.start, selection.end].map((node) => {
          if (node.sliceIndex < index) {
            return node;
          }
          return { ...node, sliceIndex: node.sliceIndex + delta };
        });

        return normalizeNodes(start, end);
      }

      // insert tokens into a slice
      if (
        path.length === 3 &&
        path[0] === "sliceMeta" &&
        path[2] === "tokenMeta"
      ) {
        const sliceIndex = +path[1];
        const [start, end] = [selection.start, selection.end].map((node) => {
          // if insert point isn't in the same slice as the node it doesn't affect the node
          if (node.sliceIndex !== sliceIndex) return node;
          // if insert position is after node it doesn't affect node
          if (op.index > node.tokenIndex) return node;
          // if the node is at or after insert position, shift right by # of tokens
          const delta = op.values.length;
          return { ...node, tokenIndex: node.tokenIndex + delta };
        });

        return normalizeNodes(start, end);
      }
      return selection;
    }

    case "array-delete": {
      // delete slice(s) from the transcript
      if (path.length === 1 && path[0] === "sliceMeta") {
        const { startIndex, endIndex } = op;
        const delta = endIndex - startIndex + 1;
        const [start, end] = [selection.start, selection.end].map((node) => {
          // if node is before slice deletion range do nothing
          if (node.sliceIndex < startIndex) return node;

          // if the node is within the slice deletion range it should be adjusted to an earlier slice
          // but we can't reference the end of earlier slices (no token / token-space equivalent)
          // so just return null
          if (node.sliceIndex >= startIndex && node.sliceIndex <= endIndex) {
            return null;
          }
          // if node is after slice deletion range, shift left by delta
          if (node.sliceIndex > endIndex) {
            // Ensure the adjusted slice index doesn't go below 0
            const adjustedSliceIndex = Math.max(0, node.sliceIndex - delta);
            return { ...node, sliceIndex: adjustedSliceIndex };
          }
          return node;
        });

        return normalizeNodes(start, end);
      }

      // delete token(s) from a slice
      if (
        path.length === 3 &&
        path[0] === "sliceMeta" &&
        path[2] === "tokenMeta"
      ) {
        const { endIndex, startIndex } = op;
        const sliceIndex = +path[1];
        const delta = endIndex - startIndex + 1;
        const [start, end] = [selection.start, selection.end].map((node) => {
          if (node.sliceIndex === sliceIndex) {
            // if node is before deletion range, do nothing
            if (node.tokenIndex < startIndex) {
              return node;
            }

            // if node is inside deletion range
            if (node.tokenIndex >= startIndex && node.tokenIndex <= endIndex) {
              // If the deletion range starts at 0, it's possible every token in the slice has been deleted
              // in which case there is no valid selection within the slice
              if (startIndex === 0) return null;
              // otherwise adjust to the end of the token before deletion range
              return {
                ...node,
                tokenIndex: startIndex - 1,
                textOffset: 0,
                type: "token-space" as const,
              };
            }
            // if node is after the deletion range, shift left by delta
            if (node.tokenIndex > endIndex) {
              return { ...node, tokenIndex: node.tokenIndex - delta };
            }
          }
          return node;
        });

        return normalizeNodes(start, end);
      }

      return selection;
    }

    default:
      assertNever(op, "Unhandled operation type");
  }
  return null;
}
